import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Auction } from '@admin-shared/models/auction';
import { StorageParameters } from '../storage.service';
import { ApiBase } from './api.base';
import { AuctionItem } from '../../models/auction-item';
import { AuctionItemMapper } from '../../mappers/auction-item.mapper';
import { AuctionMapper } from '../../mappers/auction.mapper';
export class AuctionsApi extends ApiBase {
  model = 'admin/auctions';

  public getById(id: string): Observable<Auction> {
    return this.http
      .get<Auction>(this.buildUrl(id), {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      })
      .pipe(map(AuctionMapper.mapItem));
  }

  public patch(id: string, data?: Auction): Observable<Auction> {
    return this.http.patch<Auction>(this.buildUrl(id), data, {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public deleteById(id: string): Observable<{ count: number }> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling auctionDeleteById.'
      );
    }
    return this.http.delete<{ count: number }>(this.buildUrl(id), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public startAuction(id: string): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      this.buildUrl(id, 'start'),
      undefined,
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public publishAuction(id: string, published: boolean): Observable<Auction> {
    return this.http.put<Auction>(
      this.buildUrl(id, 'publish'),
      { published },
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public postItem(id: string, data?: AuctionItem): Observable<AuctionItem> {
    return this.http.post<AuctionItem>(this.buildUrl(id, 'items'), data, {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public getItems(
    auctionId: string,
    options: {
      skip?: string;
      limit?: string;
      order?: string;
      q?: string;
      state?: string | string[];
      items?: 'true';
      includeRevenue?: boolean;
    } = {}
  ): Observable<AuctionItem[]> {
    const params = new HttpParams({ fromObject: options });
    return this.http
      .get<AuctionItem[]>(this.buildUrl(auctionId, 'items'), {
        params,
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      })
      .pipe(map(AuctionItemMapper.mapItems));
  }

  public getTotalItems(
    auctionId: string,
    options: {
      skip?: string;
      limit?: string;
      order?: string;
      q?: string;
      state?: string | string[];
      items?: 'true';
    } = {}
  ): Observable<{ total: number }> {
    const params = new HttpParams({ fromObject: options });
    return this.http.get<{ total: number }>(
      this.buildUrl(auctionId, 'items', 'total'),
      {
        params,
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getBidsReport(id: string): Observable<Array<string[]>> {
    return this.http.get<Array<string[]>>(this.buildUrl(id, 'bids-report'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public getWinnersReport(id: string): Observable<Array<(string | number)[]>> {
    return this.http.get<Array<(string | number)[]>>(
      this.buildUrl(id, 'winners-report'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getRaffleTicketReport(
    id: string
  ): Observable<Array<(string | number)[]>> {
    return this.http.get<Array<(string | number)[]>>(
      this.buildUrl(id, 'ticket-report'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getGiveawayParticipants(
    id: string
  ): Observable<Array<(string | number)[]>> {
    return this.http.get<Array<(string | number)[]>>(
      this.buildUrl(id, 'giveaway-participants'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getDonationsReport(
    id: string
  ): Observable<Array<(string | number)[]>> {
    return this.http.get<Array<(string | number)[]>>(
      this.buildUrl(id, 'donations-report'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getLocationReport(id: string): Observable<Array<(string | number)[]>> {
    return this.http.get<Array<(string | number)[]>>(
      this.buildUrl(id, 'location-report'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public availableReports(id: string) {
    return this.http.get<{
      winners: boolean;
      bidders: boolean;
      giveaway: boolean;
      ticket: boolean;
      donations: boolean;
      customBuynow: boolean;
    }>(this.buildUrl(id, 'available-reports'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public stats(
    id: string
  ): Observable<{ bidsCount: number; amountRaised: number }> {
    return this.http.get<{ bidsCount: number; amountRaised: number }>(
      this.buildUrl(id, 'auction-stats'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getQR(id: string) {
    return this.http.get<any>(this.buildUrl(id, 'qr'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  public getMarketingPdf(id: string) {
    return this.http.get<{ url: string }>(this.buildUrl(id, 'marketing-pdf'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public orderItems(id: string, items: string[]): Observable<{completed: boolean}> {
    return this.http.post<{completed: boolean}>(this.buildUrl(id, 'order-items'), {items}, {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public getCustomBuynowReport(id: string): Observable<Array<(string | number)[]>> {
    return this.http.get<Array<(string | number)[]>>(
      this.buildUrl(id, 'custom-buynow-report'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }
}
